import React, { useState, useRef } from "react";
import { Calendar, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import DropdownMenuListItem from "./DropdownMenuListItem";
import {
  Account,
  Change,
  Check,
  Clock,
  Close,
  CloseAsQualify,
  ColumnPicker,
  Delete,
  Diamond,
  Duplicate,
  Disqualify,
  Dollar,
  Edit,
  EllipsisVertical,
  ExpandView,
  Gauge,
  Hold,
  Link,
  Log,
  MarkAsProspect,
  Magnet,
  People,
  Preferences,
  Product,
  Qualify,
  Redo,
  Resolve,
  Stop,
  Trophy,
  Undo,
  UnStop,
  User,
  Add,
  Job,
  CloseDate,
  Churned,
  Contracts,
  EllipsisHorizontal,
  Hide,
  Unhide,
  Partners,
  Terminate,
  TypeTag
} from "@dreamhub/designsystem/icons";
import { useTranslation } from "react-i18next";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import IconContainer, { IconColor } from "@/components/ui/icon-container";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "@/components/ui/icon-button";
import { keys, map } from "lodash";

export const IconsMap: Record<string, JSX.Element> = {
  delete: <Delete />,
  // A proposal's signature request, as its Voided badge shows.
  withdraw: <Terminate />,
  hideField: <Hide />,
  showField: <Unhide />,
  unlink: <Link />,
  copyLink: <Link />,
  company: <Account />,
  user: <User />,
  arrMrr: <Dollar />,
  calendar: <Calendar />,
  gauge: <Gauge />,
  time: <Clock />,
  champion: <Trophy />,
  edit: <Edit />,
  disqualifyPerson: <X />,
  requalifyPerson: <Undo />,
  markAsStuck: <Stop />,
  changeBackToConfirmICP: <Change />,
  unmarkAsStuck: <UnStop />,
  reopenLead: <Undo />,
  changeToClosedQualified: <Change />,
  changeToDisqualified: <Change />,
  product: <Product />,
  markAsIcp: <Resolve />,
  convertLead: <Diamond />,
  closeLeadAsQualified: <CloseAsQualify />,
  disqualifyLead: <Disqualify />,
  editColumns: <ColumnPicker />,
  disqualifyCompany: <Disqualify />,
  requalifyCompany: <Undo />,
  markAsAnActiveCustomer: <Qualify />,
  holdForAEProspecting: <Hold />,
  expand: <ExpandView />,
  leadLog: <Log />,
  dealLog: <Log />,
  companyLog: <Log />,
  personLog: <Log />,
  contractLog: <Log />,
  partnerLog: <Log />,
  partnerContactLog: <Log />,
  duplicateContract: <Duplicate />,
  proceedTo: <Redo />,
  moveBackTo: <Undo />,
  closeWon: <Check />,
  closeLost: <Close />,
  reopenDeal: <Undo />,
  markAsProspect: <MarkAsProspect />,
  lead: <Magnet />,
  deal: <Diamond />,
  contract: <Contracts />,
  person: <People />,
  partner: <Partners />,
  partnerType: <TypeTag />,
  job: <Job />,
  expectedCloseAt: <CloseDate />,
  markAsChurned: <Churned />
};

export const renderIcon = (type: string, iconColor?: IconColor) => {
  if (IconsMap[type]) {
    return (
      <IconContainer size="5" color={iconColor || "secondary"}>
        {IconsMap[type]}
      </IconContainer>
    );
  }
  return null;
};

interface DetailedViewOptionsExtend {
  action: () => void;
  translationVars?: Record<string, any>;
  iconColor?: IconColor;
  variant?: "default" | "destructive";
  customIcon?: string | JSX.Element;
  customText?: string;
  disabled?: boolean;
  disabledTooltipText?: string;
  selected?: boolean;
  section?: string;
  secondaryText?: string;
}

export interface DetailedViewOptions {
  [key: string]: (() => void) | DetailedViewOptionsExtend;
}

export interface OptionsDropdownSection {
  id: string;
  /** Left out by a section that exists only to draw a divider, which renders
   *  no label at all. */
  title?: string;
  /** Classes for the section's label, when it has one. */
  titleClassName?: string;
  order?: number;
}

const renderDropdownItems = (
  options: DetailedViewOptions,
  onDropdownSelect: (callback: () => void) => void,
  renderIcon: (option: string, iconColor?: IconColor) => React.ReactNode,
  t: (key: string, vars?: Record<string, any>) => string,
  tPrefix: string,
  optionKeys?: string[]
) => {
  const keysToRender = optionKeys || keys(options);
  return map(keysToRender, (option) => {
    const optionValue = options[option];
    const isDetailedOption =
      typeof optionValue === "object" && "action" in optionValue;
    const action = isDetailedOption ? optionValue.action : optionValue;
    const translationVars = isDetailedOption
      ? optionValue.translationVars
      : undefined;
    const customIcon = isDetailedOption ? optionValue.customIcon : undefined;
    const customText = isDetailedOption ? optionValue.customText : undefined;
    const variant = isDetailedOption
      ? optionValue.variant || "default"
      : "default";
    const disabled = isDetailedOption ? optionValue.disabled : false;
    const disabledTooltipText = isDetailedOption
      ? optionValue.disabledTooltipText
      : undefined;
    const selected = isDetailedOption ? optionValue.selected : false;
    const secondaryText = isDetailedOption
      ? optionValue?.secondaryText
      : undefined;

    return (
      <DropdownMenuListItem
        key={option}
        option={option}
        onSelect={() => {
          onDropdownSelect(action);
        }}
        customIcon={customIcon}
        customText={customText}
        renderIcon={renderIcon}
        text={t(`${tPrefix}.${option}`, translationVars)}
        variant={variant}
        disabled={disabled}
        disabledTooltipText={disabledTooltipText}
        selected={selected}
        data-test={option}
        secondaryText={secondaryText}
      />
    );
  });
};

const renderSectionedDropdownItems = (
  options: DetailedViewOptions,
  groupedOptions: Record<string, string[]>,
  orderedSectionIds: string[],
  getSectionTitle: (sectionId: string) => string,
  getSectionTitleClassName: (sectionId: string) => string | undefined,
  onDropdownSelect: (callback: () => void) => void,
  renderIcon: (option: string, iconColor?: IconColor) => React.ReactNode,
  t: (key: string, vars?: Record<string, any>) => string,
  tPrefix: string,
  showSectionDividers: boolean
) => {
  return orderedSectionIds.map((sectionId, index) => {
    const sectionOptions = groupedOptions[sectionId];
    const sectionTitle = getSectionTitle(sectionId);
    const isLastSection = index === orderedSectionIds.length - 1;

    return (
      <React.Fragment key={sectionId}>
        {/* A menu can group its items purely to draw dividers between them, and
            an untitled section should not push an empty label into the list. */}
        {sectionTitle && (
          <DropdownMenuLabel
            className={cn(
              "px-2 pt-2 pb-[2px]",
              getSectionTitleClassName(sectionId)
            )}
          >
            <Text variant="smBodySecondary">{sectionTitle}</Text>
          </DropdownMenuLabel>
        )}
        {renderDropdownItems(
          options,
          onDropdownSelect,
          renderIcon,
          t,
          tPrefix,
          sectionOptions
        )}
        {showSectionDividers && !isLastSection && (
          <DropdownMenuSeparator className="h-px" />
        )}
      </React.Fragment>
    );
  });
};

interface Props {
  options: DetailedViewOptions;
  tPrefix: string;
  topOptions?: DetailedViewOptions;
  topOptionsTitle?: string;
  optionsTitle?: string;
  buttonVariant?: IconButtonVariant;
  iconColor?: IconColor;
  iconSize?: IconButtonSize;
  className?: string;
  contentClassName?: string;
  align?: "start" | "center" | "end";
  onOpenChange?: (open: boolean) => void;
  showIcons?: boolean;
  triggerIcon?: "ellipsis" | "add" | "preferences" | "horizontal-ellipsis";
  customTrigger?: React.ReactNode;
  triggerDataTest?: string;
  tooltipText?: string;
  enableSections?: boolean;
  sections?: OptionsDropdownSection[];
  defaultSectionTitle?: string;
  showSectionDividers?: boolean;
  disabled?: boolean;
  disableCloseAutoFocus?: boolean;
}

const OptionsDropdownMenu: React.FC<Props> = ({
  options,
  tPrefix,
  topOptions,
  topOptionsTitle,
  optionsTitle,
  buttonVariant = "icon",
  iconColor,
  iconSize,
  className,
  contentClassName,
  align = "end",
  onOpenChange,
  showIcons = true,
  triggerIcon = "ellipsis",
  customTrigger,
  triggerDataTest,
  tooltipText,
  enableSections = false,
  sections = [],
  defaultSectionTitle = "",
  showSectionDividers = true,
  disabled = false,
  disableCloseAutoFocus = false
}) => {
  const { t } = useTranslation();

  const { onDropdownOpenChange, onDropdownSelect } = useDropdownClosing();

  const [isOpen, setIsOpen] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);

  // Group options by section when sections are enabled
  const groupedOptions = React.useMemo((): Record<string, string[]> => {
    if (!enableSections) {
      return { default: keys(options) };
    }

    const groups: Record<string, string[]> = {};

    keys(options).forEach((optionKey) => {
      const optionValue = options[optionKey];
      const isDetailedOption =
        typeof optionValue === "object" && "action" in optionValue;
      const sectionId = isDetailedOption
        ? optionValue.section || "default"
        : "default";

      if (!groups[sectionId]) {
        groups[sectionId] = [];
      }
      groups[sectionId].push(optionKey);
    });

    return groups;
  }, [options, enableSections]);

  const getSectionTitle = React.useCallback(
    (sectionId: string) => {
      if (sectionId === "default") {
        return defaultSectionTitle;
      }
      // A section with no title is a divider-only group and takes no label. An
      // id never stands in for one: `orderedSectionIds` only ever holds
      // declared sections, so falling back to the id put the raw id on screen
      // as a heading rather than covering any case where a title was missing.
      return sections.find((s) => s.id === sectionId)?.title ?? "";
    },
    [sections, defaultSectionTitle]
  );

  const getSectionTitleClassName = React.useCallback(
    (sectionId: string) =>
      sections.find((s) => s.id === sectionId)?.titleClassName,
    [sections]
  );

  const orderedSectionIds = React.useMemo(() => {
    if (!enableSections) {
      return Object.keys(groupedOptions);
    }

    return sections
      .map((section) => section.id)
      .filter((id) => groupedOptions[id]);
  }, [groupedOptions, sections, enableSections]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onDropdownOpenChange(open);
    onOpenChange?.(open);

    // Prevent focus on trigger when dropdown closes
    if (!open) {
      setTimeout(() => {
        const active = document.activeElement as HTMLElement | null;
        if (active && (active === buttonRef.current || customTrigger)) {
          active.blur();
        }
      }, 0);
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const buttonComponent = (
    <IconButton
      ref={buttonRef}
      data-test={triggerDataTest}
      variant={buttonVariant}
      size={iconSize}
      iconColor={iconColor}
      className={cn(
        "transition-colors duration-200 ease-in-out",
        isOpen && buttonVariant !== "iconNoBg" && "bg-surface-focus-selected",
        className,
        isOpen && "opacity-100"
      )}
      onClick={handleButtonClick}
      title=""
      onFocus={(e) => {
        // Prevent focus if dropdown just closed
        if (!isOpen && e.target === buttonRef.current) {
          e.target.blur();
        }
      }}
      disabled={disabled}
    >
      {triggerIcon === "add" ? (
        <Add />
      ) : triggerIcon === "preferences" ? (
        <Preferences />
      ) : triggerIcon === "horizontal-ellipsis" ? (
        <EllipsisHorizontal />
      ) : (
        <EllipsisVertical />
      )}
    </IconButton>
  );

  return (
    <DropdownMenu onOpenChange={handleOpenChange} open={isOpen}>
      {tooltipText ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild data-test="3-dots-menu">
              {customTrigger || buttonComponent}
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent enableMarkdown={false}>{tooltipText}</TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenuTrigger asChild data-test="3-dots-menu">
          {customTrigger || buttonComponent}
        </DropdownMenuTrigger>
      )}
      <DropdownMenuContent
        align={align}
        sideOffset={7}
        className={cn("flex flex-col min-w-[14rem]", contentClassName)}
        onClick={handleButtonClick}
        onCloseAutoFocus={
          disableCloseAutoFocus ? (e) => e.preventDefault() : undefined
        }
      >
        {keys(topOptions || {}).length > 0 && (
          <>
            {topOptionsTitle && (
              <DropdownMenuLabel className="px-2 pt-2 pb-[2px]">
                <Text variant="smBodySecondary">{topOptionsTitle}</Text>
              </DropdownMenuLabel>
            )}
            {renderDropdownItems(
              topOptions || {},
              onDropdownSelect,
              showIcons ? renderIcon : () => null,
              t,
              tPrefix
            )}
          </>
        )}

        {keys(topOptions || {}).length > 0 && keys(options).length > 0 && (
          <DropdownMenuSeparator className="h-px" />
        )}

        {keys(options).length > 0 && (
          <>
            {enableSections ? (
              renderSectionedDropdownItems(
                options,
                groupedOptions,
                orderedSectionIds,
                getSectionTitle,
                getSectionTitleClassName,
                onDropdownSelect,
                showIcons ? renderIcon : () => null,
                t,
                tPrefix,
                showSectionDividers
              )
            ) : (
              <>
                {optionsTitle && (
                  <DropdownMenuLabel className="px-2 pt-2 pb-[2px]">
                    <Text variant="smBodySecondary">{optionsTitle}</Text>
                  </DropdownMenuLabel>
                )}
                {renderDropdownItems(
                  options,
                  onDropdownSelect,
                  showIcons ? renderIcon : () => null,
                  t,
                  tPrefix
                )}
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OptionsDropdownMenu;
